/**
 * This is the main entrypoint file for the application.
 *
 * When loaded in the client side, the application is rendered in the
 * #root element.
 *
 * When the bundle created from this file is imported in the server
 * side, the exported `renderApp` function can be used for server side
 * rendering.
 *
 * Note that this file is required for the build process.
 */

// Dependency libs
import React from 'react';
import ReactDOMClient from 'react-dom/client';
import { loadableReady } from '@loadable/component';

// Import default styles before other CSS-related modules are imported
// This ensures that the styles in marketplaceDefaults.css are included
// as first ones in the final build CSS build file.
import './styles/marketplaceDefaults.css';
import './styles/delowo-tokens.css';
import './styles/custom_delowo.css';

// Configs and store setup
import appSettings from './config/settings';
import defaultConfig from './config/configDefault';
import { LoggingAnalyticsHandler, GoogleAnalyticsHandler } from './analytics/handlers';
import configureStore from './store';

// Utils
import { createInstance, types as sdkTypes } from './util/sdkLoader';
import { mergeConfig } from './util/configHelpers';
import { matchPathname } from './util/routes';
import * as apiUtils from './util/api';
import * as log from './util/log';

// Import relevant global duck files
import { authInfo } from './ducks/auth.duck';
import { fetchAppAssets } from './ducks/hostedAssets.duck';
import { fetchCurrentUser } from './ducks/user.duck';

// Route config
import routeConfiguration from './routing/routeConfiguration';
// App it self
import { ClientApp, renderApp } from './app';
import { loadTopbar } from './containers/TopbarContainer/TopbarContainer';

// The web-font faces every account page renders with (the @font-face rules in public/index.html):
// Inter 400–700 and the Manrope 800 headings, Latin and Cyrillic.
const ACCOUNT_PAGE_FONTS = ['400', '500', '600', '700']
  .map(w => `${w} 1em Inter`)
  .concat('800 1em Manrope');
const loadAccountPageFonts = () =>
  document.fonts?.load
    ? Promise.all(ACCOUNT_PAGE_FONTS.map(font => document.fonts.load(font, 'Aa Аа')))
    : Promise.resolve();

const render = (store, shouldHydrate) => {
  // If the server already loaded the auth information, render the app
  // immediately. Otherwise wait for the flag to be loaded and render
  // when auth information is present.
  const state = store.getState();
  const cdnAssetsVersion = state.hostedAssets.version;
  const authInfoLoaded = state.auth.authInfoLoaded;
  const info = authInfoLoaded ? Promise.resolve({}) : store.dispatch(authInfo());

  info
    .then(() => {
      // Ensure that Loadable Components is ready
      // and fetch hosted assets in parallel before initializing the ClientApp.
      // The current user is NOT awaited here: the server never loads the session, so the
      // hydrating render of a public page must see the same `currentUser: null` as the SSR
      // markup (backlog #4, H1-a); it is fetched right after the root is created, below.
      return Promise.all([
        loadableReady(),
        store.dispatch(fetchAppAssets(defaultConfig.appCdnAssets, cdnAssetsVersion)),
      ]);
    })
    .then(([_, fetchedAppAssets]) => {
      const { translations: translationsRaw, ...rest } = fetchedAppAssets || {};
      // We'll handle translations as a separate data.
      // It's given to React Intl instead of pushing to config Context
      const translations = translationsRaw?.data || {};

      // Rest of the assets are considered as hosted configs
      const configEntries = Object.entries(rest);
      const hostedConfig = configEntries.reduce((collectedData, [name, content]) => {
        return { ...collectedData, [name]: content.data || {} };
      }, {});

      // Routes with `auth: true` get no route content from the server (nothing to hydrate against),
      // so they keep waiting for the user as before: the page then renders with its data in one go
      // instead of an empty shell that fills up when the user arrives (measured: CLS 0.00 → 0.6 on
      // /account and /listings without this). Public routes hydrate first and fetch the user after.
      // The Topbar chunk and the web fonts are loaded alongside (backlog #4 C5 / C6): the server sent
      // no chunks for these routes and no text has used the fonts yet, so a topbar arriving after the
      // page, or a font swap after the first render, moved the page. Neither failure is fatal, and
      // the fonts never delay the render past the user.
      const appConfig = mergeConfig(hostedConfig, defaultConfig);
      const routes = routeConfiguration(appConfig.layout, appConfig.accessControl);
      const isAuthRoute = matchPathname(window.location.pathname, routes).some(m => !!m.route.auth);
      const user = isAuthRoute ? store.dispatch(fetchCurrentUser()) : null;
      const userBeforeRender = isAuthRoute
        ? Promise.all([
            user,
            loadTopbar().catch(() => null),
            Promise.race([loadAccountPageFonts(), user]).catch(() => null),
          ])
        : Promise.resolve();

      return userBeforeRender.then(() => {
        if (shouldHydrate) {
          const container = document.getElementById('root');

          ReactDOMClient.hydrateRoot(
            container,
            <ClientApp
              store={store}
              hostedTranslations={translations}
              hostedConfig={hostedConfig}
            />,
            { onRecoverableError: log.onRecoverableError }
          );
        } else {
          const container = document.getElementById('root');
          const root = ReactDOMClient.createRoot(container);
          root.render(
            <ClientApp
              store={store}
              hostedTranslations={translations}
              hostedConfig={hostedConfig}
            />
          );
        }

        // Public routes: load the current user after the first client render has been scheduled —
        // the role-dependent UI (topbar CTA, landing panels) switches when the user arrives, without
        // discarding the server-rendered DOM. A failed fetch is already logged by the thunk
        // (fetch-current-user-failed) and must not be reported as a failed render.
        return isAuthRoute ? null : store.dispatch(fetchCurrentUser()).catch(() => null);
      });
    })
    .catch(e => {
      log.error(e, 'browser-side-render-failed');
    });
};

const setupAnalyticsHandlers = googleAnalyticsId => {
  let handlers = [];

  // Log analytics page views and events in dev mode
  if (appSettings.dev) {
    handlers.push(new LoggingAnalyticsHandler());
  }

  // Add Google Analytics 4 (GA4) handler if tracker ID is found
  if (googleAnalyticsId) {
    if (googleAnalyticsId.indexOf('G-') !== 0) {
      console.warn(
        'Google Analytics 4 (GA4) should have measurement id that starts with "G-" prefix'
      );
    } else {
      handlers.push(new GoogleAnalyticsHandler());
    }
  }

  return handlers;
};

// If we're in a browser already, render the client application.
if (typeof window !== 'undefined') {
  // set up logger with Sentry DSN client key and environment
  log.setup();

  const baseUrl = appSettings.sdk.baseUrl ? { baseUrl: appSettings.sdk.baseUrl } : {};
  const assetCdnBaseUrl = appSettings.sdk.assetCdnBaseUrl
    ? { assetCdnBaseUrl: appSettings.sdk.assetCdnBaseUrl }
    : {};

  // eslint-disable-next-line no-underscore-dangle
  const preloadedState = window.__PRELOADED_STATE__ || '{}';
  const initialState = JSON.parse(preloadedState, sdkTypes.reviver);
  const sdk = createInstance({
    transitVerbose: appSettings.sdk.transitVerbose,
    clientId: appSettings.sdk.clientId,
    secure: appSettings.usingSSL,
    typeHandlers: apiUtils.typeHandlers,
    ...baseUrl,
    ...assetCdnBaseUrl,
  });

  // Note: on localhost:3000, you need to use environment variable.
  const googleAnalyticsIdFromSSR = initialState?.hostedAssets?.googleAnalyticsId;
  const googleAnalyticsId = googleAnalyticsIdFromSSR || process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
  const analyticsHandlers = setupAnalyticsHandlers(googleAnalyticsId);
  const store = configureStore({ initialState, sdk, analyticsHandlers });

  require('./util/polyfills');
  render(store, !!window.__PRELOADED_STATE__);

  // Dev-only visual annotation toolbar (Agentation). The dynamic import inside
  // this guard is dropped from production builds.
  if (process.env.NODE_ENV === 'development') {
    import('agentation')
      .then(({ Agentation }) => {
        // Reuse an already mounted toolbar (e.g. after hot reload) instead of adding a second root
        if (document.getElementById('agentation-root')) {
          return;
        }
        const container = document.createElement('div');
        container.id = 'agentation-root';
        document.body.appendChild(container);
        ReactDOMClient.createRoot(container).render(<Agentation />);
      })
      .catch(e => console.warn('Agentation failed to load', e));
  }

  if (appSettings.dev) {
    // Expose stuff for the browser REPL
    window.app = {
      appSettings,
      defaultConfig,
      sdk,
      sdkTypes,
      store,
    };
  }
}

// Show warning if CSP is not enabled
const CSP = process.env.REACT_APP_CSP;
const cspEnabled = CSP === 'block' || CSP === 'report';

if (CSP === 'report' && process.env.REACT_APP_ENV === 'production') {
  console.warn(
    'Your production environment should use CSP with "block" mode. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/'
  );
} else if (!cspEnabled) {
  console.warn(
    "CSP is currently not enabled! You should add an environment variable REACT_APP_CSP with the value 'report' or 'block'. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/"
  );
}

// Export the function for server side rendering.
export default renderApp;

// exporting matchPathname and configureStore for server side rendering.
// matchPathname helps to figure out which route is called and if it has preloading needs
// configureStore is used for creating initial store state for Redux after preloading
export {
  matchPathname,
  configureStore,
  routeConfiguration,
  defaultConfig,
  mergeConfig,
  fetchAppAssets,
};
